import React, { FC, useState } from "react";

import { redirectToAirflowGraphUrl } from "api/ScheduledTasksApi";
import Search from "components/basic/search/Search";
import { useHasPermissions } from "hooks/useHasPermissions";
import airflowLogo from "img/airflow-icon16.png";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { T } from "services/lang/LanguageService";
import { AirflowDag, AirflowTaskFilter, airflowTaskTypes } from "types/airflow";
import AirflowDagForm from "./AirflowDagForm";
import AirflowTaskForm from "./AirflowTaskForm";

type TasksHeaderProps = {
  dag?: AirflowDag;
  filter: AirflowTaskFilter;
  setFilter: React.Dispatch<React.SetStateAction<AirflowTaskFilter>>;
};

const filterOptions = [
  { label: T("word.type"), items: airflowTaskTypes.map(type => ({ ...type, value: { type: type.value } })) },
  {
    label: T("word.sends-alerts"),
    items: [
      { label: T("word.yes"), value: { sendAlerts: true } },
      { label: T("word.no"), value: { sendAlerts: false } },
    ],
  },
];

const TasksHeader: FC<TasksHeaderProps> = ({ dag, filter, setFilter }) => {
  const [createTaskDialog, setCreateTaskDialog] = useState(false);
  const [editDagDialog, setEditDagDialog] = useState(false);
  const canEdit = useHasPermissions("AIRFLOW_EDIT");

  return (
    <div className="flex justify-content-between px-3 py-2">
      {dag && canEdit && (
        <div className="flex gap-2">
          <Button
            label={T("action.create-task")}
            icon="pi pi-plus"
            className="dashboard-editor-button icon-green border-green"
            onClick={() => setCreateTaskDialog(true)}
          />
          <Button
            label={`${T("action.edit")} DAG`}
            icon="pi pi-pencil"
            className="dashboard-editor-button icon-orange border-orange"
            onClick={() => setEditDagDialog(true)}
          />
        </div>
      )}
      <div className="flex gap-2">
        <Search
          value={filter.search}
          onChange={val => setFilter({ ...filter, search: val })}
          placeholder={T("action.search-for-tasks")}
        />
        <MultiSelect
          options={filterOptions}
          value={filter.filters}
          onChange={val => setFilter({ ...filter, filters: val.value })}
          style={{ height: "2.6rem" }}
          className="w-10rem"
          selectedItemsLabel={`{0} ${T("word.filters")}`}
          maxSelectedLabels={1}
          placeholder={T("word.filters")}
          optionGroupLabel="label"
          optionGroupChildren="items"
          optionLabel="label"
        />
      </div>
      {dag && canEdit && (
        <div>
          <a href={redirectToAirflowGraphUrl(dag.dagId)} target="_blank" rel="noreferrer">
            <Button
              label={`Airflow DAG ${T("word.graph")}`}
              type="button"
              icon={<img src={airflowLogo} alt="" className="mr-2" />}
              className="dashboard-editor-button border-black"
              severity="contrast"
            />
          </a>
        </div>
      )}
      <Dialog
        visible={createTaskDialog}
        header="Create new task"
        onHide={() => setCreateTaskDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowTaskForm closeDialog={() => setCreateTaskDialog(false)} dag={dag!} />
      </Dialog>

      <Dialog
        visible={editDagDialog}
        header="Edit DAG"
        onHide={() => setEditDagDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowDagForm closeDialog={() => setEditDagDialog(false)} dag={dag} />
      </Dialog>
    </div>
  );
};

export default TasksHeader;
